/**
 * Group Chat Response Mode Helpers
 *
 * Shared helpers for group chat assistant response behavior.
 *
 * Primary responsibilities:
 * - Read response mode from chat metadata
 * - Merge response mode updates into chat metadata
 * - Detect @mentions that should trigger group AI responses
 * - Let `@Losi` act as an in-thread assistant mention
 * - Let human mentions suppress Losi in always-reply group mode
 *
 * Dependencies:
 * - Supabase Json type
 */

import type { Json } from '@/supabase/types'
import {
  hasEveryoneMention,
  hasPlatformLosiMention
} from '@/lib/chat/mentions'

export type GroupChatResponseMode = 'always' | 'mention_only'

const DEFAULT_RESPONSE_MODE: GroupChatResponseMode = 'always'
const MENTION_PATTERN = /(^|[\s([{<])@losi(?![a-z0-9_.-])/i
const GROUP_MENTION_TOKEN_PATTERN =
  /(^|[\s([{<])@([a-z0-9_][a-z0-9_]{0,63})(?![a-z0-9_.-])/gi

export const GROUP_CHAT_MENTION_ONLY_ERROR_MESSAGE =
  'Group chat is in mention-only mode. Mention @Losi to get a response.'

function asJsonObject(value: Json | null | undefined): Record<string, Json> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as Record<string, Json>
}

export function getGroupChatResponseMode(
  metadata: Json | null | undefined
): GroupChatResponseMode {
  const metadataObject = asJsonObject(metadata)
  const groupChatObject = asJsonObject(metadataObject?.groupChat ?? null)
  const rawResponseMode = groupChatObject?.responseMode

  if (isGroupChatResponseMode(rawResponseMode)) {
    return rawResponseMode
  }

  return DEFAULT_RESPONSE_MODE
}

export function isGroupChatResponseMode(
  value: unknown
): value is GroupChatResponseMode {
  return value === 'always' || value === 'mention_only'
}

export function mergeGroupChatResponseModeMetadata(
  metadata: Json | null | undefined,
  responseMode: GroupChatResponseMode
): Json {
  const metadataObject = asJsonObject(metadata)
  const groupChatObject = asJsonObject(metadataObject?.groupChat ?? null)

  return {
    ...(metadataObject ?? {}),
    groupChat: {
      ...(groupChatObject ?? {}),
      enabled: true,
      responseMode
    }
  } as Json
}

export function isGroupChatAssistantMentioned(messageText: string): boolean {
  if (!messageText.trim()) {
    return false
  }

  return MENTION_PATTERN.test(messageText) || hasPlatformLosiMention(messageText)
}

export function getGroupChatMentionTokens(messageText: string): string[] {
  if (!messageText.trim()) {
    return []
  }

  const tokens = new Set<string>()

  for (const match of messageText.matchAll(GROUP_MENTION_TOKEN_PATTERN)) {
    const token = match[2]?.trim().toLowerCase()

    if (token) {
      tokens.add(token)
    }
  }

  return [...tokens]
}

export function isGroupChatHumanMentioned(messageText: string): boolean {
  if (hasEveryoneMention(messageText)) {
    return true
  }

  return getGroupChatMentionTokens(messageText).some((token) => token !== 'losi')
}

function isKnownGroupChatHumanMentioned(
  messageText: string,
  knownHumanMentionTokens: readonly string[] | undefined
): boolean {
  const knownTokens = new Set(
    (knownHumanMentionTokens ?? [])
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)
  )

  if (knownTokens.size === 0) {
    return hasEveryoneMention(messageText)
  }

  return (
    getGroupChatMentionTokens(messageText).some(
      (token) =>
        token !== 'losi' &&
        (token === 'everyone' || knownTokens.has(token))
    ) || hasEveryoneMention(messageText)
  )
}

export function isGroupChatMentionOnlyErrorMessage(message: string): boolean {
  return message.trim() === GROUP_CHAT_MENTION_ONLY_ERROR_MESSAGE
}

export function isGroupChatMentionOnlyConflictError(error: unknown): boolean {
  return error instanceof Error && isGroupChatMentionOnlyErrorMessage(error.message)
}

export function shouldEnableChatInputAssistantAutocomplete({
  isGroupChat,
  isStructuredMentionMode,
  isGroupMentionOnlyMode = false,
  enableGroupHumanMentions = false
}: {
  isGroupChat: boolean
  isStructuredMentionMode: boolean
  isGroupMentionOnlyMode?: boolean
  enableGroupHumanMentions?: boolean
}) {
  if (isStructuredMentionMode) {
    return false
  }

  if (isGroupChat) {
    return isGroupMentionOnlyMode || enableGroupHumanMentions
  }

  return true
}

export function shouldSkipAlwaysModeGroupAssistantForHumanMention({
  isGroupChat,
  metadata,
  messageText,
  knownHumanMentionTokens,
  knownHumanMentionTokensReady = false
}: {
  isGroupChat: boolean
  metadata: Json | null | undefined
  messageText: string
  isRegeneration?: boolean
  knownHumanMentionTokens?: readonly string[]
  knownHumanMentionTokensReady?: boolean
}) {
  if (!isGroupChat) {
    return false
  }

  if (getGroupChatResponseMode(metadata) !== 'always') {
    return false
  }

  if (isGroupChatAssistantMentioned(messageText)) {
    return false
  }

  if (!knownHumanMentionTokensReady) {
    return isGroupChatHumanMentioned(messageText)
  }

  return isKnownGroupChatHumanMentioned(messageText, knownHumanMentionTokens)
}

export function shouldSkipGroupChatAssistantResponse({
  isGroupChat,
  metadata,
  messageText,
  isRegeneration = false
}: {
  isGroupChat: boolean
  metadata: Json | null | undefined
  messageText: string
  isRegeneration?: boolean
}) {
  if (!isGroupChat || isRegeneration) {
    return false
  }

  const responseMode = getGroupChatResponseMode(metadata)
  if (responseMode !== 'mention_only') {
    return false
  }

  return !isGroupChatAssistantMentioned(messageText)
}
